import React, { useState, useEffect } from 'react';
import { Calculator, RefreshCw, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import MailingCostDateRangeSelector from '../../components/ppc-farm/MailingCostDateRangeSelector';
import MailingCostKPICards from '../../components/ppc-farm/MailingCostKPICards';
import MailingCostTabbedViewer from '../../components/ppc-farm/MailingCostTabbedViewer';
import {
  PpcFarmCostService,
  DateRange,
  MailingCostKPIs,
  DirectMailSpendRow,
  DataListCostRow,
  MaterialsOtherRow
} from '../../services/ppcFarmCostService';
import { usePageTitle } from '../../hooks/usePageTitle';

const MailingCostDashboard: React.FC = () => {
  usePageTitle('PPC Farm - Mailing Cost Dashboard');

  // State management
  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [kpis, setKpis] = useState<MailingCostKPIs>({
    totalDirectMailSpend: 0,
    totalDataScrapeSpend: 0,
    totalOtherCosts: 0,
    grandTotalSpend: 0
  });
  const [directMailData, setDirectMailData] = useState<DirectMailSpendRow[]>([]);
  const [dataListData, setDataListData] = useState<DataListCostRow[]>([]);
  const [materialsOtherData, setMaterialsOtherData] = useState<MaterialsOtherRow[]>([]);
  
  // Loading states
  const [isLoadingKPIs, setIsLoadingKPIs] = useState(false);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Load data when date range changes
  useEffect(() => {
    loadAllData();
  }, [dateRange]);

  // Initial load
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setError(null);
      setIsLoadingKPIs(true);
      setIsLoadingTables(true);

      // Load KPIs and table data in parallel
      const [kpiData, directMail, dataList, materialsOther] = await Promise.all([
        PpcFarmCostService.getKPIData(dateRange),
        PpcFarmCostService.getDirectMailSpendBreakdown(dateRange),
        PpcFarmCostService.getDataListCosts(dateRange),
        PpcFarmCostService.getMaterialsOtherExpenses(dateRange)
      ]);

      setKpis(kpiData);
      setDirectMailData(directMail);
      setDataListData(dataList);
      setMaterialsOtherData(materialsOther);
      setLastUpdated(new Date());

    } catch (error) {
      console.error('Error loading mailing cost data:', error);
      setError('Failed to load mailing cost data. Please try again.');
    } finally {
      setIsLoadingKPIs(false);
      setIsLoadingTables(false);
    }
  };

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
  };

  const handleRefresh = () => {
    loadAllData();
  };

  const formatLastUpdated = (date: Date | null): string => {
    if (!date) return 'Never';
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Mailing Cost Dashboard
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Comprehensive analysis of direct mail campaign costs and expenses
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      )}

      {/* Date Range Selector */}
      <div className="mb-8">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <MailingCostDateRangeSelector
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
              />
              <div className="flex items-center space-x-4">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Last updated: {formatLastUpdated(lastUpdated)}
                </div>
                <Button
                  onClick={handleRefresh}
                  disabled={isLoadingKPIs || isLoadingTables}
                  size="sm"
                  variant="outline"
                  className="flex items-center space-x-2"
                >
                  <RefreshCw className={`h-4 w-4 ${(isLoadingKPIs || isLoadingTables) ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* KPI Cards */}
      <div className="mb-8">
        <MailingCostKPICards
          kpis={kpis}
          isLoading={isLoadingKPIs}
        />
      </div>

      {/* Tabbed Tables */}
      <div className="mb-8">
        <MailingCostTabbedViewer
          directMailData={directMailData}
          dataListData={dataListData}
          materialsOtherData={materialsOtherData}
          isLoading={isLoadingTables}
          dateRange={dateRange}
        />
      </div>
    </div>
  );
};

export default MailingCostDashboard;